/**
 * COMMAND — every business's WhatsApp and texts, who has each one, and how
 * fast we answer. House staff only.
 *
 * Anthony, 2026-10-24: "best of the best, as if Apple took twenty years to
 * build a business monster tool", with GoHighLevel kept as the engine.
 * Four views on one page:
 *
 *   Board   the live numbers by business and by person, refreshed every
 *           fifteen seconds while the page is open
 *   Inbox   one list for every business; open a thread beside it
 *   Rules   who gets what, a test box, and what the router decided
 *   Team    who holds what and who is waiting on whom
 *
 * Everything goes through /api/command (server/routes/command.ts). Sending
 * is a person pressing Send. Jev's draft sits above the reply box and goes
 * nowhere unless somebody puts it in the box and sends it.
 */

import { api, db } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";

type Biz = { key: string; name: string; color: string; whatsapp: string | null; catch_whatsapp: boolean;
  in_today: number; out_today: number; whatsapp_today: number; in_week: number; new_today: number;
  waiting: number; overdue: number; oldest_wait: number | null; reply_today: number | null; reply_week: number | null; unowned: number };
type Person = { id: string; name: string; open: number; waiting: number; overdue: number; oldest_wait: number | null; sent_today: number };
type Board = { me: string; role: string; mode: "watch" | "write"; escalate_minutes: number; at: string;
  businesses: Biz[]; hours: Array<{ h: number; in: number; out: number }>; team: Person[];
  jev: { open: number; drafts_today: number; drafting: boolean };
  totals: { in_today: number; waiting: number; overdue: number; new_today: number; reply_today: number | null };
  error?: string; missing?: boolean };
type Thread = { conversation_id: string; name: string | null; phone10: string | null; business_key: string | null;
  site_key: string | null; channel: "whatsapp" | "sms"; lang: string | null; last_in_at: string | null; last_out_at: string | null;
  last_body: string | null; last_direction: string | null; owner_kind: string | null; owner_user_id: string | null;
  owner_name: string | null; wrote_ghl_at: string | null; status: string; waiting: number | null; wa_open: boolean; jev: { at?: string; text?: string } | null };

type View = "board" | "inbox" | "rules" | "team";
let view: View = "board";
let timer: number | null = null;
let filters: Record<string, string> = { status: "open" };
let openId: string | null = null;
let lastBoard: Board | null = null;

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

async function call<T>(path: string, body?: unknown): Promise<{ ok: boolean; status: number; out: T & { error?: string } }> {
  const r = await fetch(api(path), {
    method: body === undefined ? "GET" : "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${await bearer()}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const out = await r.json().catch(() => ({})) as T & { error?: string };
  return { ok: r.ok, status: r.status, out };
}

const mins = (m: number | null | undefined): string => {
  if (m === null || m === undefined) return "–";
  if (m < 1) return "<1m";
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ${Math.round(m % 60)}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
};
const phone = (p: string | null) => p && p.length === 10 ? `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}` : "";
const when = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  const same = d.toDateString() === new Date().toDateString();
  return same ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const chan = (c: string) => c === "whatsapp" ? `<span class="cc-chan wa">WhatsApp</span>` : `<span class="cc-chan sms">Text</span>`;

/** #/command/<view>, and #/command/inbox/<conversation id> opens that one. */
export async function commandPage(arg?: string, open?: string): Promise<void> {
  if (arg === "inbox" || arg === "rules" || arg === "team" || arg === "board") view = arg;
  if (open) { view = "inbox"; openId = open; }
  mount(`<div class="page cc-page">
    <header class="page-head cc-head">
      <div><h1>Command</h1>
        <p>Every business's WhatsApp and texts in one place. GoHighLevel holds the threads; this decides who has each one and shows how fast we answer.</p></div>
      <div class="cc-live" id="cc-live"><i></i><span>Live</span></div>
    </header>
    <div id="cc-mode"></div>
    <div class="tabs wide" id="cc-tabs">
      ${(["board", "inbox", "rules", "team"] as View[]).map((v) =>
        `<button class="tab${v === view ? " on" : ""}" data-view="${v}">${v === "board" ? "Board" : v === "inbox" ? "Inbox" : v === "rules" ? "Rules" : "Team"}</button>`).join("")}
    </div>
    <div id="cc-body"><div class="loading">Reading&hellip;</div></div>
  </div>`);
  document.getElementById("cc-tabs")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-view]");
    if (!b) return;
    view = b.dataset["view"] as View;
    document.querySelectorAll("#cc-tabs .tab").forEach((t) => t.classList.toggle("on", t === b));
    void draw();
  });
  if (timer) window.clearInterval(timer);
  /* LIVE, BUT ONLY WHILE SOMEBODY IS LOOKING. A hidden tab polling every
     fifteen seconds all night is GHL calls and database reads for nobody,
     and the board reads both. Stops itself when the page is left. */
  timer = window.setInterval(() => {
    if (!document.getElementById("cc-body")) { if (timer) window.clearInterval(timer); timer = null; return; }
    if (document.hidden) return;
    if (view === "board" || view === "team") void draw(true);
    if (view === "inbox") void drawList(true);
  }, 15_000);
  await draw();
}

async function draw(quiet = false): Promise<void> {
  const body = document.getElementById("cc-body");
  if (!body) return;
  if (!quiet) body.innerHTML = `<div class="loading">Reading&hellip;</div>`;
  if (view === "inbox") return void inbox(body);
  if (view === "rules") return void rules(body);
  const r = await call<Board>("/api/command/board").catch(() => null);
  if (!r) { body.innerHTML = `<div class="empty">Could not reach the server. Trying again in a moment.</div>`; return; }
  if (r.status === 404) { body.innerHTML = `<div class="empty">This page is for the Supercanic house team.</div>`; return; }
  if (!r.ok) { body.innerHTML = `<div class="empty">${esc(r.out.error ?? "Could not read the board.")}</div>`; return; }
  lastBoard = r.out;
  modeBanner(r.out.mode);
  body.innerHTML = view === "team" ? teamView(r.out) : boardView(r.out);
  body.querySelectorAll<HTMLElement>("[data-goto]").forEach((el) => el.addEventListener("click", () => {
    const [k, v] = (el.dataset["goto"] ?? "").split("=");
    filters = { status: "open", ...(k && v ? { [k]: v } : {}) };
    view = "inbox";
    document.querySelectorAll("#cc-tabs .tab").forEach((t) => t.classList.toggle("on", (t as HTMLElement).dataset["view"] === "inbox"));
    void draw();
  }));
}

function modeBanner(mode: string): void {
  const el = document.getElementById("cc-mode");
  if (!el) return;
  el.innerHTML = mode === "watch"
    ? `<div class="cc-banner watch"><b>Watching.</b> The router picks an owner for every new conversation and writes it here only. Nothing is written into GoHighLevel until it is switched to write. Assigning by hand still goes to GoHighLevel.</div>`
    : `<div class="cc-banner write"><b>Routing live.</b> New conversations get their owner and tags in GoHighLevel. Nothing is ever sent on its own.</div>`;
}

// ─── Board ───────────────────────────────────────────────────────────────

function kpi(label: string, value: string, tone = "", goto = ""): string {
  return `<div class="cc-kpi ${tone}"${goto ? ` data-goto="${esc(goto)}" role="button" tabindex="0"` : ""}>
    <div class="cc-kpi-v">${value}</div><div class="cc-kpi-l">${esc(label)}</div></div>`;
}

function hoursChart(hours: Board["hours"]): string {
  const max = Math.max(1, ...hours.map((h) => Math.max(h.in, h.out)));
  const now = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", hour12: false }).format(new Date())) % 24;
  const W = 24 * 14, H = 80;
  const bars = hours.map((h) => {
    const x = h.h * 14;
    const hi = Math.round((h.in / max) * (H - 14));
    const ho = Math.round((h.out / max) * (H - 14));
    return `<g${h.h > now ? ' class="cc-future"' : ""}><title>${h.h}:00 · ${h.in} in, ${h.out} out</title>
      <rect x="${x + 2}" y="${H - 12 - hi}" width="5" height="${hi}" rx="1.5" class="cc-bar-in"/>
      <rect x="${x + 7}" y="${H - 12 - ho}" width="5" height="${ho}" rx="1.5" class="cc-bar-out"/></g>`;
  }).join("");
  const ticks = [0, 6, 12, 18].map((h) => `<text x="${h * 14 + 1}" y="${H - 1}" class="cc-tick">${h === 0 ? "12a" : h === 12 ? "12p" : h < 12 ? `${h}a` : `${h - 12}p`}</text>`).join("");
  return `<svg viewBox="0 0 ${W} ${H}" class="cc-hours" role="img" aria-label="Messages by hour today">${bars}${ticks}</svg>
    <div class="cc-legend"><span><i class="in"></i>From customers</span><span><i class="out"></i>From us</span></div>`;
}

function boardView(b: Board): string {
  const t = b.totals;
  return `
  <div class="cc-kpis">
    ${kpi("Messages in today", String(t.in_today))}
    ${kpi("Waiting on us", String(t.waiting), t.waiting ? "warn" : "", "waiting=1")}
    ${kpi(`Over ${b.escalate_minutes} minutes`, String(t.overdue), t.overdue ? "bad" : "good", "waiting=1")}
    ${kpi("New conversations", String(t.new_today))}
    ${kpi("First reply, today", mins(t.reply_today), t.reply_today !== null && t.reply_today > b.escalate_minutes ? "warn" : "")}
  </div>
  <div class="cc-grid">
    <div>
      ${b.businesses.map((x) => `
      <div class="card cc-biz" style="--biz:${esc(x.color)}" data-goto="business=${esc(x.key)}" role="button" tabindex="0">
        <div class="cc-biz-top"><span class="cc-dot"></span><b>${esc(x.name)}</b>
          ${x.catch_whatsapp ? `<span class="cc-chan wa">WhatsApp${x.whatsapp ? "" : " · number not set"}</span>` : ""}
          ${x.overdue ? `<span class="cc-flag bad">${x.overdue} overdue</span>` : x.waiting ? `<span class="cc-flag warn">${x.waiting} waiting</span>` : `<span class="cc-flag good">All answered</span>`}</div>
        <div class="cc-biz-stats">
          <div><b>${x.in_today}</b><span>in today</span></div>
          <div><b>${x.out_today}</b><span>out today</span></div>
          <div><b>${x.new_today}</b><span>new</span></div>
          <div><b>${mins(x.reply_today)}</b><span>first reply</span></div>
          <div><b>${x.oldest_wait === null ? "–" : mins(x.oldest_wait)}</b><span>longest wait</span></div>
          <div><b>${x.unowned}</b><span>no owner</span></div>
        </div>
        <div class="cc-biz-foot">${x.in_week} in the last 7 days · first reply ${mins(x.reply_week)} over the week</div>
      </div>`).join("")}
    </div>
    <div class="cc-side">
      <div class="card pad"><div class="cc-sub">Today, hour by hour</div>${hoursChart(b.hours)}</div>
      <div class="card pad"><div class="cc-sub">People</div>
        ${b.team.length ? b.team.map((p) => `
          <div class="cc-person" data-goto="owner=${esc(p.id)}" role="button" tabindex="0">
            <span class="cc-avatar">${esc(initials(p.name))}</span>
            <span class="cc-pname">${esc(p.name)}</span>
            <span class="cc-pstat">${p.open} open</span>
            ${p.overdue ? `<span class="cc-flag bad">${p.overdue} late</span>` : p.waiting ? `<span class="cc-flag warn">${p.waiting} waiting</span>` : ""}
          </div>`).join("") : `<div class="empty">Nobody holds a conversation yet.</div>`}
        <div class="cc-person" data-goto="owner=jev" role="button" tabindex="0">
          <span class="cc-avatar jev">J</span><span class="cc-pname">Jev, drafting</span>
          <span class="cc-pstat">${b.jev.drafts_today} drafts today</span></div>
      </div>
    </div>
  </div>
  <p class="cc-stamp">Updated ${esc(when(b.at))}. Refreshes every 15 seconds.</p>`;
}

function initials(n: string): string {
  return n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";
}

function teamView(b: Board): string {
  return `<div class="card">
    <div class="card-head">Who holds what</div>
    ${b.team.map((p) => `
      <div class="row cc-team-row" data-goto="owner=${esc(p.id)}" role="button" tabindex="0"><div class="row-main">
        <div class="row-title"><span class="cc-avatar">${esc(initials(p.name))}</span> ${esc(p.name)}</div>
        <div class="row-sub">${p.open} open · ${p.waiting} waiting on them${p.oldest_wait !== null ? ` · longest ${mins(p.oldest_wait)}` : ""} · ${p.sent_today} sent today</div>
      </div>${p.overdue ? `<span class="cc-flag bad">${p.overdue} late</span>` : ""}</div>`).join("")
      || `<div class="empty">Nobody holds a conversation yet.</div>`}
  </div>
  <p class="cc-stamp">Shifts and who gets what are on the Rules tab.</p>`;
}

// ─── Inbox ───────────────────────────────────────────────────────────────

async function inbox(body: HTMLElement): Promise<void> {
  /* The chips, colours and names come from the board. Opened straight on
     the inbox (a deep link), the board has not been read yet. */
  if (!lastBoard) {
    const r = await call<Board>("/api/command/board").catch(() => null);
    if (r?.ok) { lastBoard = r.out; modeBanner(r.out.mode); }
  }
  const biz = lastBoard?.businesses ?? [];
  const team = lastBoard?.team ?? [];
  const chip = (k: string, v: string, label: string) =>
    `<button class="cc-chip${(filters[k] ?? "") === v ? " on" : ""}" data-f="${k}" data-v="${esc(v)}">${esc(label)}</button>`;
  body.innerHTML = `
  <div class="cc-filters">
    <div class="cc-chips">${chip("business", "", "Every business")}${biz.map((b) => chip("business", b.key, b.name)).join("")}</div>
    <div class="cc-chips">${chip("channel", "", "All")}${chip("channel", "whatsapp", "WhatsApp")}${chip("channel", "sms", "Texts")}
      <span class="cc-gap"></span>${chip("waiting", "", "Everything")}${chip("waiting", "1", "Waiting on us")}
      <span class="cc-gap"></span>${chip("owner", "", "Anyone")}${chip("owner", "none", "No owner")}${chip("owner", "jev", "Jev")}${team.map((p) => chip("owner", p.id, p.name.split(" ")[0] ?? p.name)).join("")}
      <span class="cc-gap"></span>${chip("status", "open", "Open")}${chip("status", "closed", "Closed")}</div>
    <input class="cc-search" id="cc-q" type="search" placeholder="Search a name, number or words" value="${esc(filters["q"] ?? "")}">
  </div>
  <div class="cc-split${openId ? " has-open" : ""}" id="cc-split">
    <div class="card cc-list" id="cc-list"><div class="loading">Reading&hellip;</div></div>
    <div class="card cc-thread" id="cc-thread">${openId ? `<div class="loading">Opening&hellip;</div>` : `<div class="empty">Pick a conversation.</div>`}</div>
  </div>`;
  body.querySelectorAll<HTMLElement>("[data-f]").forEach((el) => el.addEventListener("click", () => {
    const k = el.dataset["f"]!, v = el.dataset["v"] ?? "";
    if (v) filters[k] = v; else delete filters[k];
    void inbox(body);
  }));
  let typing: number | null = null;
  document.getElementById("cc-q")?.addEventListener("input", (e) => {
    if (typing) window.clearTimeout(typing);
    typing = window.setTimeout(() => {
      const v = (e.target as HTMLInputElement).value.trim();
      if (v) filters["q"] = v; else delete filters["q"];
      void drawList();
    }, 300);
  });
  await drawList();
  if (openId) await openThread(openId);
}

async function drawList(quiet = false): Promise<void> {
  const host = document.getElementById("cc-list");
  if (!host) return;
  const q = new URLSearchParams(filters);
  const r = await call<{ threads: Thread[] }>(`/api/command/inbox?${q}`).catch(() => null);
  if (!r?.ok) { if (!quiet) host.innerHTML = `<div class="empty">${esc(r?.out.error ?? "Could not read the inbox.")}</div>`; return; }
  const colors = new Map((lastBoard?.businesses ?? []).map((b) => [b.key, b.color]));
  const names = new Map((lastBoard?.businesses ?? []).map((b) => [b.key, b.name]));
  host.innerHTML = r.out.threads.length ? r.out.threads.map((t) => `
    <button class="cc-row${t.conversation_id === openId ? " on" : ""}${t.waiting !== null && t.waiting >= (lastBoard?.escalate_minutes ?? 15) ? " late" : ""}"
      data-open="${esc(t.conversation_id)}" style="--biz:${esc(colors.get(t.business_key ?? "") ?? "#64748b")}">
      <span class="cc-row-bar"></span>
      <span class="cc-row-main">
        <span class="cc-row-top"><b>${esc(t.name || phone(t.phone10) || "Unknown")}</b>
          <span class="cc-row-when">${esc(when(t.last_direction === "inbound" ? t.last_in_at : t.last_out_at))}</span></span>
        <span class="cc-row-body">${t.last_direction === "outbound" ? "You: " : ""}${esc((t.last_body ?? "").slice(0, 110))}</span>
        <span class="cc-row-meta">${chan(t.channel)} <span>${esc(names.get(t.business_key ?? "") ?? "No business")}</span>
          ${t.site_key ? `<span class="cc-site">${esc(t.site_key)}</span>` : ""}
          ${t.lang === "es" ? `<span class="cc-site">ES</span>` : ""}
          <span class="cc-owner">${t.owner_name ? esc(t.owner_name) : "No owner"}${t.owner_name && !t.wrote_ghl_at && t.owner_kind === "human" ? " (suggested)" : ""}</span>
          ${t.waiting !== null ? `<span class="cc-flag ${t.waiting >= (lastBoard?.escalate_minutes ?? 15) ? "bad" : "warn"}">${mins(t.waiting)}</span>` : ""}
          ${t.jev ? `<span class="cc-flag jev">Draft</span>` : ""}</span>
      </span>
    </button>`).join("") : `<div class="empty">Nothing here. ${filters["waiting"] ? "Nobody is waiting on us." : ""}</div>`;
  host.querySelectorAll<HTMLElement>("[data-open]").forEach((el) => el.addEventListener("click", () => {
    openId = el.dataset["open"]!;
    host.querySelectorAll(".cc-row").forEach((x) => x.classList.toggle("on", x === el));
    document.getElementById("cc-split")?.classList.add("has-open");
    void openThread(openId);
  }));
}

type ThreadAnswer = { thread: Thread; business: { name: string; color: string } | null; site: { name: string } | null;
  messages: Array<{ id: string; channel: string; direction: string; body: string; at: string; user_id: string | null }>;
  live: boolean; users: Array<{ id: string; name: string }>; log: Array<{ at: string; mode: string; rule_name: string | null; owner_name: string | null; why: string | null; wrote: boolean; error: string | null }>;
  templates: Array<{ id: string; name: string; type: string; body: string }>; mode: string; ghl_url: string };

async function openThread(id: string): Promise<void> {
  const host = document.getElementById("cc-thread");
  if (!host) return;
  host.innerHTML = `<div class="loading">Opening&hellip;</div>`;
  const r = await call<ThreadAnswer>(`/api/command/thread/${encodeURIComponent(id)}`).catch(() => null);
  if (!r?.ok) { host.innerHTML = `<div class="empty">${esc(r?.out.error ?? "Could not open it.")}</div>`; return; }
  const a = r.out, t = a.thread;
  const names = new Map(a.users.map((u) => [u.id, u.name]));
  const canFree = t.channel === "sms" || t.wa_open;
  const quick = a.templates.filter((x) => x.type === (t.channel === "whatsapp" ? "whatsapp" : "sms") || x.type === "sms");
  host.innerHTML = `
    <div class="cc-th-head" style="--biz:${esc(a.business?.color ?? "#64748b")}">
      <button class="btn ghost sm cc-back" id="cc-back">&larr;</button>
      <div class="cc-th-who"><b>${esc(t.name || phone(t.phone10) || "Unknown")}</b>
        <span>${esc(phone(t.phone10))} · ${chan(t.channel)} · ${esc(a.business?.name ?? "No business")}${a.site ? ` · ${esc(a.site.name)}` : ""}</span></div>
      <a class="btn ghost sm" href="${esc(a.ghl_url)}" target="_blank" rel="noopener">Open in GHL</a>
    </div>
    <div class="cc-th-tools">
      <label class="cc-assign">Owner
        <select id="cc-owner">
          <option value="">Nobody</option>
          <option value="jev"${t.owner_kind === "agent" ? " selected" : ""}>Jev (drafts only)</option>
          ${a.users.map((u) => `<option value="${esc(u.id)}"${u.id === t.owner_user_id ? " selected" : ""}>${esc(u.name)}</option>`).join("")}
        </select></label>
      ${t.owner_kind === "human" && !t.wrote_ghl_at ? `<span class="cc-flag warn">Router's pick, not in GHL yet</span>` : ""}
      <span class="cc-spacer"></span>
      ${t.status === "open" ? `<button class="btn sm" id="cc-close">Done</button>` : `<button class="btn sm" id="cc-reopen">Reopen</button>`}
    </div>
    <div class="cc-msgs" id="cc-msgs">
      ${a.messages.map((m) => `<div class="cc-msg ${m.direction === "inbound" ? "in" : "out"}">
        <div class="cc-bubble">${esc(m.body) || "<i>(no words, a picture or a file)</i>"}</div>
        <div class="cc-msg-meta">${esc(when(m.at))}${m.channel !== t.channel ? ` · ${m.channel === "whatsapp" ? "WhatsApp" : "text"}` : ""}${
          m.direction === "outbound" ? ` · ${esc(m.user_id ? names.get(m.user_id) ?? "Someone in GHL" : "Automated")}` : ""}</div></div>`).join("")
        || `<div class="empty">No messages yet.</div>`}
      ${a.live ? "" : `<p class="cc-stamp">GoHighLevel did not answer, so this is our last copy.</p>`}
    </div>
    ${t.jev?.text ? `<div class="cc-jev"><div class="cc-sub">Jev's draft <span>· not sent, yours to use or ignore</span></div>
      <div class="cc-jev-text">${esc(t.jev.text)}</div>
      ${canFree ? `<button class="btn sm" id="cc-usejev">Put it in the box</button>` : ""}</div>` : ""}
    <div class="cc-compose">
      ${canFree ? `
        ${quick.length ? `<select id="cc-quick" class="cc-quick"><option value="">Quick replies</option>${quick.map((q) => `<option value="${esc(q.id)}">${esc(q.name)}</option>`).join("")}</select>` : ""}
        <textarea id="cc-text" rows="3" placeholder="${t.lang === "es" ? "Escribe tu respuesta" : "Type your reply"}"></textarea>
        <div class="cc-compose-foot"><span class="cc-stamp">${t.channel === "whatsapp" ? "Sends on WhatsApp through GoHighLevel." : "Sends as a text from this business's line."}</span>
          <button class="btn primary" id="cc-send">Send</button></div>
        <p class="msg bad" id="cc-send-msg" hidden></p>`
      : `<div class="cc-banner watch">It has been more than 24 hours since they wrote on WhatsApp. Meta only allows an approved template now. <a href="${esc(a.ghl_url)}" target="_blank" rel="noopener">Send one from GHL</a>.</div>`}
    </div>
    ${a.log.length ? `<details class="cc-log"><summary>What the router did</summary>${a.log.map((l) =>
      `<div>${esc(when(l.at))} · ${esc(l.mode)} · ${esc(l.owner_name ?? "nobody")} · ${esc(l.why ?? "")}${l.error ? ` · <b>${esc(l.error)}</b>` : ""}</div>`).join("")}</details>` : ""}`;

  const msgs = document.getElementById("cc-msgs");
  if (msgs) msgs.scrollTop = msgs.scrollHeight;
  document.getElementById("cc-back")?.addEventListener("click", () => {
    openId = null; document.getElementById("cc-split")?.classList.remove("has-open");
  });
  document.getElementById("cc-owner")?.addEventListener("change", async (e) => {
    const v = (e.target as HTMLSelectElement).value;
    const res = await call(`/api/command/thread/${encodeURIComponent(id)}/assign`, { user_id: v || null });
    if (!res.ok) alert(res.out.error ?? "Not assigned.");
    void openThread(id); void drawList(true);
  });
  for (const [btn, verb] of [["cc-close", "close"], ["cc-reopen", "reopen"]] as const) {
    document.getElementById(btn)?.addEventListener("click", async () => {
      const res = await call(`/api/command/thread/${encodeURIComponent(id)}/${verb}`, {});
      if (!res.ok) alert(res.out.error ?? "Nothing changed.");
      void openThread(id); void drawList(true);
    });
  }
  const box = document.getElementById("cc-text") as HTMLTextAreaElement | null;
  document.getElementById("cc-usejev")?.addEventListener("click", () => {
    if (box && t.jev?.text) { box.value = t.jev.text.replace(/^PERSONA:[^\n]*\n?/, "").trim(); box.focus(); }
  });
  document.getElementById("cc-quick")?.addEventListener("change", (e) => {
    const q = quick.find((x) => x.id === (e.target as HTMLSelectElement).value);
    if (q && box) { box.value = box.value ? `${box.value}\n${q.body}` : q.body; box.focus(); }
    (e.target as HTMLSelectElement).value = "";
  });
  document.getElementById("cc-send")?.addEventListener("click", async (e) => {
    const btn = e.target as HTMLButtonElement;
    const msg = document.getElementById("cc-send-msg");
    const text = box?.value.trim() ?? "";
    if (!text) return;
    btn.disabled = true; btn.textContent = "Sending…";
    const res = await call(`/api/command/thread/${encodeURIComponent(id)}/send`, { message: text });
    btn.disabled = false; btn.textContent = "Send";
    if (!res.ok) { if (msg) { msg.hidden = false; msg.textContent = res.out.error ?? "Not sent."; } return; }
    void openThread(id); void drawList(true);
  });
}

// ─── Rules ───────────────────────────────────────────────────────────────

type RuleRow = { id: string; position: number; name: string; enabled: boolean; business_key: string | null;
  match: { channel?: string; site?: string; words?: string[]; lang?: string; from_hour?: number; to_hour?: number };
  assign: { kind: "human"; users: string[] } | { kind: "agent"; agent: string }; tags: string[] };
type RulesAnswer = { mode: string; rules: RuleRow[]; businesses: Array<{ key: string; name: string; lines: string[]; whatsapp10: string | null; catch_whatsapp: boolean }>;
  sites: Array<{ key: string; name: string; business_key: string; openers: string[]; playbook_length: number }>;
  users: Array<{ id: string; name: string }>; log: Array<{ at: string; conversation_id: string; mode: string; rule_name: string | null; owner_name: string | null; why: string | null; wrote: boolean; error: string | null }>;
  may_edit: boolean; may_switch: boolean };

const hr = (h: number) => h === 0 ? "midnight" : h === 12 ? "noon" : h < 12 ? `${h}am` : `${h - 12}pm`;

function matchSummary(r: RuleRow, a: RulesAnswer): string {
  const m = r.match ?? {};
  const bits: string[] = [];
  if (r.business_key) bits.push(a.businesses.find((b) => b.key === r.business_key)?.name ?? r.business_key);
  if (m.channel) bits.push(m.channel === "whatsapp" ? "WhatsApp" : "texts");
  if (m.site) bits.push(`from ${a.sites.find((s) => s.key === m.site)?.name ?? m.site}`);
  if (m.lang) bits.push(m.lang === "es" ? "written in Spanish" : "written in English");
  if (m.words?.length) bits.push(`says ${m.words.map((w) => `"${w}"`).join(" or ")}`);
  if (m.from_hour !== undefined && m.to_hour !== undefined) bits.push(`${hr(m.from_hour)} to ${hr(m.to_hour)}`);
  return bits.length ? bits.join(", ") : "anything";
}

async function rules(body: HTMLElement): Promise<void> {
  const r = await call<RulesAnswer>("/api/command/rules").catch(() => null);
  if (!r?.ok) { body.innerHTML = `<div class="empty">${esc(r?.out.error ?? "Could not read the rules.")}</div>`; return; }
  const a = r.out;
  modeBanner(a.mode);
  const who = (x: RuleRow) => x.assign.kind === "agent" ? "Jev (drafts only)"
    : x.assign.users.map((u) => a.users.find((p) => p.id === u)?.name ?? u).join(" and ") || "nobody";
  body.innerHTML = `
  <div class="cc-grid">
    <div>
      <div class="card">
        <div class="card-head">Who gets what, top to bottom. The first rule that fits wins.</div>
        ${[...a.rules].sort((x, y) => x.position - y.position).map((x) => `
          <div class="row cc-rule${x.enabled ? "" : " off"}"><div class="row-main">
            <div class="row-title"><span class="cc-pos">${x.position}</span> ${esc(x.name)} ${x.enabled ? "" : `<span class="pill">Off</span>`}</div>
            <div class="row-sub">When it is ${esc(matchSummary(x, a))}, it goes to <b>${esc(who(x))}</b>.</div>
            ${a.may_edit ? `<div class="cc-rule-edit">
              <button class="btn ghost sm" data-toggle="${esc(x.id)}">${x.enabled ? "Turn off" : "Turn on"}</button>
              <button class="btn ghost sm" data-edit="${esc(x.id)}">Change</button></div>` : ""}
            <div class="cc-rule-form" data-form="${esc(x.id)}" hidden></div>
          </div></div>`).join("")}
        ${a.may_edit ? `<div class="row"><div class="row-main"><button class="btn sm" data-edit="new">Add a rule</button>
          <div class="cc-rule-form" data-form="new" hidden></div></div></div>` : ""}
      </div>
      <div class="card">
        <div class="card-head">Businesses and sites</div>
        ${a.businesses.map((b) => `<div class="row"><div class="row-main">
          <div class="row-title">${esc(b.name)}</div>
          <div class="row-sub">Texts on ${b.lines.map((l) => esc(phone(l))).join(", ") || "no lines"}${b.catch_whatsapp ? ` · WhatsApp ${b.whatsapp10 ? esc(phone(b.whatsapp10)) : "(number not set yet, takes every WhatsApp)"}` : b.whatsapp10 ? ` · WhatsApp ${esc(phone(b.whatsapp10))}` : ""}</div>
          ${a.sites.filter((s) => s.business_key === b.key).map((s) => `<div class="row-note">${esc(s.name)}: opens with ${s.openers.map((o) => `"${esc(o)}"`).join(" or ")} · ${s.playbook_length ? "playbook written" : "no playbook yet, Jev keeps to greetings"}</div>`).join("")}
        </div></div>`).join("")}
      </div>
    </div>
    <div class="cc-side">
      <div class="card pad"><div class="cc-sub">Try a message</div>
        <label class="field"><span>What they wrote</span><textarea id="cc-try" rows="2" placeholder="Hola, vi FiestaCerca y quiero saber el precio"></textarea></label>
        <div class="cc-try-row">
          <label class="field"><span>On</span><select id="cc-try-ch"><option value="whatsapp">WhatsApp</option><option value="sms">Text</option></select></label>
          <label class="field"><span>Our line (texts)</span><select id="cc-try-line"><option value="">–</option>${a.businesses.flatMap((b) => b.lines.map((l) => `<option value="${esc(l)}">${esc(b.name)} ${esc(phone(l))}</option>`)).join("")}</select></label>
          <label class="field"><span>Hour</span><select id="cc-try-h"><option value="">Now</option>${Array.from({ length: 24 }, (_, h) => `<option value="${h}">${hr(h)}</option>`).join("")}</select></label>
        </div>
        <button class="btn sm" id="cc-try-go">Who would get it</button>
        <div id="cc-try-out" class="cc-try-out"></div>
      </div>
      ${a.may_switch ? `<div class="card pad"><div class="cc-sub">The router</div>
        <p class="cc-stamp">${a.mode === "watch" ? "Watching. It decides and writes here only." : "Writing owners and tags into GHL."}</p>
        <button class="btn sm" id="cc-mode-btn">${a.mode === "watch" ? "Start writing to GHL" : "Go back to watching"}</button></div>` : ""}
      <div class="card"><div class="card-head">What the router decided</div>
        ${a.log.slice(0, 30).map((l) => `<div class="row"><div class="row-main">
          <div class="row-sub">${esc(when(l.at))} · ${esc(l.owner_name ?? "nobody")} · ${esc(l.why ?? "")}</div>
          <div class="row-note">${esc(l.mode)}${l.wrote ? " · written to GHL" : ""}${l.error ? ` · ${esc(l.error)}` : ""}</div></div></div>`).join("")
          || `<div class="empty">Nothing yet. Every new conversation shows here.</div>`}
      </div>
    </div>
  </div>`;

  body.querySelectorAll<HTMLElement>("[data-toggle]").forEach((el) => el.addEventListener("click", async () => {
    const x = a.rules.find((y) => y.id === el.dataset["toggle"]);
    if (!x) return;
    const res = await call("/api/command/rules", { ...x, enabled: !x.enabled });
    if (!res.ok) alert(res.out.error ?? "Not saved."); void rules(body);
  }));
  body.querySelectorAll<HTMLElement>("[data-edit]").forEach((el) => el.addEventListener("click", () => {
    const id = el.dataset["edit"]!;
    const x = a.rules.find((y) => y.id === id);
    const form = body.querySelector<HTMLElement>(`[data-form="${id}"]`);
    if (!form) return;
    form.hidden = !form.hidden;
    if (form.hidden) return;
    const m = x?.match ?? {};
    const users = x?.assign.kind === "human" ? x.assign.users : [];
    form.innerHTML = `
      <label class="field"><span>Name</span><input data-k="name" value="${esc(x?.name ?? "")}"></label>
      <div class="cc-try-row">
        <label class="field"><span>Order</span><input data-k="position" type="number" value="${x?.position ?? 50}"></label>
        <label class="field"><span>Business</span><select data-k="business_key"><option value="">Any</option>${a.businesses.map((b) => `<option value="${esc(b.key)}"${b.key === x?.business_key ? " selected" : ""}>${esc(b.name)}</option>`).join("")}</select></label>
        <label class="field"><span>Channel</span><select data-k="channel"><option value="">Any</option><option value="whatsapp"${m.channel === "whatsapp" ? " selected" : ""}>WhatsApp</option><option value="sms"${m.channel === "sms" ? " selected" : ""}>Texts</option></select></label>
      </div>
      <div class="cc-try-row">
        <label class="field"><span>Site</span><select data-k="site"><option value="">Any</option>${a.sites.map((s) => `<option value="${esc(s.key)}"${s.key === m.site ? " selected" : ""}>${esc(s.name)}</option>`).join("")}</select></label>
        <label class="field"><span>Language</span><select data-k="lang"><option value="">Any</option><option value="es"${m.lang === "es" ? " selected" : ""}>Spanish</option><option value="en"${m.lang === "en" ? " selected" : ""}>English</option></select></label>
        <label class="field"><span>From</span><select data-k="from_hour"><option value="">Any time</option>${Array.from({ length: 24 }, (_, h) => `<option value="${h}"${m.from_hour === h ? " selected" : ""}>${hr(h)}</option>`).join("")}</select></label>
        <label class="field"><span>To</span><select data-k="to_hour"><option value="">–</option>${Array.from({ length: 24 }, (_, h) => `<option value="${h}"${m.to_hour === h ? " selected" : ""}>${hr(h)}</option>`).join("")}</select></label>
      </div>
      <label class="field"><span>Words in the message, comma between</span><input data-k="words" value="${esc((m.words ?? []).join(", "))}"></label>
      <div class="field"><span>Goes to</span><div class="cc-chips">
        <label class="cc-chip"><input type="checkbox" data-u="jev"${x?.assign.kind === "agent" ? " checked" : ""}> Jev (drafts only)</label>
        ${a.users.map((u) => `<label class="cc-chip"><input type="checkbox" data-u="${esc(u.id)}"${users.includes(u.id) ? " checked" : ""}> ${esc(u.name)}</label>`).join("")}</div></div>
      <button class="btn primary sm" data-save="${esc(id)}">Save</button>`;
    form.querySelector(`[data-save]`)?.addEventListener("click", async () => {
      const v = (k: string) => (form.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-k="${k}"]`)?.value ?? "").trim();
      const picked = Array.from(form.querySelectorAll<HTMLInputElement>("[data-u]")).filter((c) => c.checked).map((c) => c.dataset["u"]!);
      const match: Record<string, unknown> = {};
      if (v("channel")) match["channel"] = v("channel");
      if (v("site")) match["site"] = v("site");
      if (v("lang")) match["lang"] = v("lang");
      if (v("from_hour") !== "" && v("to_hour") !== "") { match["from_hour"] = Number(v("from_hour")); match["to_hour"] = Number(v("to_hour")); }
      const words = v("words").split(",").map((w) => w.trim()).filter(Boolean);
      if (words.length) match["words"] = words;
      const res = await call("/api/command/rules", {
        ...(x ? { id: x.id, enabled: x.enabled, tags: x.tags } : {}), name: v("name"), position: Number(v("position")),
        business_key: v("business_key") || null, match,
        assign: picked.includes("jev") ? { kind: "agent", agent: "jev" } : { kind: "human", users: picked },
      });
      if (!res.ok) { alert(res.out.error ?? "Not saved."); return; }
      void rules(body);
    });
  }));
  document.getElementById("cc-try-go")?.addEventListener("click", async () => {
    const out = document.getElementById("cc-try-out");
    const h = (document.getElementById("cc-try-h") as HTMLSelectElement).value;
    const res = await call<{ business: string | null; site: string | null; lang: string; rule: string | null; owner: string | null; tags: string[]; why: string }>(
      "/api/command/test", {
        text: (document.getElementById("cc-try") as HTMLTextAreaElement).value,
        channel: (document.getElementById("cc-try-ch") as HTMLSelectElement).value,
        line: (document.getElementById("cc-try-line") as HTMLSelectElement).value,
        ...(h !== "" ? { hour: Number(h) } : {}),
      });
    if (!out) return;
    out.innerHTML = res.ok
      ? `<div><b>${esc(res.out.owner ?? "Nobody")}</b> gets it.</div>
         <div class="row-note">${esc(res.out.business ?? "No business")}${res.out.site ? ` · ${esc(res.out.site)}` : ""} · ${res.out.lang === "es" ? "Spanish" : "English"} · ${esc(res.out.why)}</div>
         ${res.out.tags.length ? `<div class="row-note">Tags in GHL: ${res.out.tags.map(esc).join(", ")}</div>` : ""}`
      : `<p class="msg bad">${esc(res.out.error ?? "Could not test it.")}</p>`;
  });
  document.getElementById("cc-mode-btn")?.addEventListener("click", async () => {
    const next = a.mode === "watch" ? "write" : "watch";
    if (next === "write" && !confirm("From now on the router sets the owner and tags in GoHighLevel for every new conversation. It still never sends anything. Start?")) return;
    const res = await call("/api/command/mode", { mode: next });
    if (!res.ok) alert(res.out.error ?? "Not switched."); void rules(body);
  });
}
